import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames/bind';
import get from 'lodash/get';
import Type from './type';
import styles from './Icon.css';
import SvgPortal from '../../../SvgPortal/SvgPortal';
import { shallowCompareObjects } from '../../../comparators/objectComparators';
import { dt } from '../../../helpers/Utils/GetTailwindToken';
import withPerformanceMeasurement from '../../../hoc/PerformanceObserver/HOCPerfMeasure';

const cx = classNames.bind(styles);

/**
 * Icon component to standardize the styling and html structure of any Icon component on the page
 * Examples of using this component:
 *
 * Variant 1: `type="svg"`:
 * `<Icon type="svg" width="16" height="16" viewBox="0 0 16 16" pathValue="M608 192h160v..."/>`
 *
 * Variant 2: `type="icon"`:
 *
 * `<Icon type="icon" classNames='fa fa-cloud' />`
 */
class Icon extends Component {
    /**
     * PropTypes to Indicate types of each Props for the entire component
     * Supported React properties
     * @type {Object}
     */
    static propTypes = {
        /**
         * classes that can be passed to component
         * so that it can be added to the element
         */
        className: PropTypes.string,
        /**
         * classes that can be passed to component
         * using this we can fill color to svg and many class changes
         */
        pathClassName: PropTypes.string,
        /**
         * Styles passed to a component
         */
        inLineStyle: PropTypes.object,
        /**
         * Type of Icon that needs to be created
         * 1) svg: Icons using SVG
         *    Props needed for SVG: width, height, viewBox, pathValue and classes
         * 2) icon: Span for Icons
         *    Props needed for Icons are classes
         *    Default type : icon
         */
        iconType: PropTypes.string,
        /**
         * Width attribute for SVG element
         */
        width: PropTypes.string,
        /**
         * Height attribute for SVG element
         */
        height: PropTypes.string,
        /**
         * View box attribute for SVG
         */
        viewBox: PropTypes.string,
        /**
         * Unique name for referencing dom element in automation testing
         * @type {String}
         */
        automationId: PropTypes.string,
        children: PropTypes.object,
    };

    static defaultProps = {
        iconType: Type.ICON,
        className: '',
        width: '16px',
        height: '16px',
        viewBox: '0 0 1024 1024',
        automationId: '',
        children: null,
        pathClassName: '',
        inLineStyle: {},
    };

    shouldComponentUpdate(nextProps) {
        const { children: nChildren, ...nProps } = nextProps;
        const { children: pChildren, ...pProps } = this.props;
        const nChild = React.Children.only(nChildren);
        const pChild = React.Children.only(pChildren);

        return !(
            shallowCompareObjects(nProps, pProps) &&
            nChild &&
            pChild &&
            nChild.type.displayName === pChild.type.displayName
        );
    }

    render() {
        const {
            className,
            iconType,
            width,
            height,
            viewBox,
            inLineStyle,
            automationId,
            pathClassName,
            children,
        } = this.props;
        const iconMainStyle = dt(['inline-block', 'pointer-events-none']);
        const iconClasses = `${iconMainStyle} ${cx('icon-main', ...className.split(' '))}`;
        if (iconType === Type.SVG) {
            const name = get(children, 'type.displayName', '');
            if (name === '') {
                return null;
            }
            if (children.props.id) {
                throw new Error(
                    'It is not supposed to pass id property to the icon, please remove it'
                );
            }
            return (
                <>
                    <SvgPortal>
                        {React.isValidElement(children) &&
                            React.cloneElement(children, { id: name })}
                    </SvgPortal>
                    <svg
                        data-automation-id={automationId}
                        className={iconClasses}
                        width={width}
                        height={height}
                        viewBox={viewBox}
                        style={inLineStyle}
                    >
                        <use xlinkHref={`#${name}`} className={pathClassName} />
                    </svg>
                </>
            );
        }

        return <span data-automation-id={automationId} className={iconClasses} />;
    }
}

export default withPerformanceMeasurement(Icon);
